/** Scoped approval detail state; storage-only aggregate counters never enter the public UI model. */
import { WritableSignal } from '@angular/core';
import { Entity, VisitApprovalEffectiveValidityCounts, VisitApprovalReviewContext, VisitApprovalScope, VisitApprovalScopedCounts, VisitApprovalScopedRevision, VisitApprovalSummary } from '@jms/model';

export interface VisitApprovalRecordRef {
  _id: string;
  name: string;
  customer: Entity;
  reviewContext: VisitApprovalReviewContext;
  currentRevision: number;
}

export type VisitApprovalDetailModel = VisitApprovalSummary;

/** What an approval's child boards know that their rows do not; read with `resolveBoardParams`. */
export interface ApprovalRevisionsBoardParams {
  readonly approval: VisitApprovalRecordRef;
  readonly scope: VisitApprovalScope;
}

export interface ApprovalVisitsBoardParams extends ApprovalRevisionsBoardParams {
  /** Pins a historical revision. Absent, the board reads the approval as it stands now. */
  readonly revision?: VisitApprovalScopedRevision;
  /** The mount's membership lock, separate from the always-readonly evidence form. */
  readonly hostReadonly: boolean;
  readonly canRemove: boolean;
  /** Per mount, so a second removal cannot start while one is committing. */
  readonly busy: WritableSignal<boolean>;
  /** Rereads the host approval so its `Visits (N)` title and expected revision follow the commit. */
  readonly changed?: () => Promise<void>;
}

export function readVisitApprovalScope(value: unknown): VisitApprovalScope | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = 'scope' in value ? value.scope : value;
  if (!candidate || typeof candidate !== 'object' || !('customerId' in candidate) || typeof candidate.customerId !== 'string' || !candidate.customerId) return null;

  return {
    customerId: candidate.customerId,
    siteId: 'siteId' in candidate && typeof candidate.siteId === 'string' && candidate.siteId ? candidate.siteId : undefined,
    servicePlanId: 'servicePlanId' in candidate && typeof candidate.servicePlanId === 'string' && candidate.servicePlanId ? candidate.servicePlanId : undefined,
  };
}

export function toVisitApprovalDetailModel(summary: VisitApprovalSummary, name: string): VisitApprovalDetailModel {
  return {
    _id: summary._id,
    name,
    customer: summary.customer,
    reviewContext: {
      label: summary.reviewContext.label,
      from: summary.reviewContext.from,
      to: summary.reviewContext.to,
      timezone: summary.reviewContext.timezone,
      note: summary.reviewContext.note ?? '',
    },
    currentRevision: summary.currentRevision,
    changedAt: summary.changedAt,
    changedBy: summary.changedBy,
    membership: copyMembership(summary.membership),
    validity: copyValidity(summary.validity),
    servicePlans: summary.servicePlans?.map(plan => ({ _id: plan._id, name: plan.name })) ?? [],
    servicePlanCount: summary.servicePlanCount ?? 0,
  };
}

function copyMembership(membership: VisitApprovalScopedCounts): VisitApprovalScopedCounts {
  return membership.fullTotal === undefined
    ? { matchedTotal: membership.matchedTotal }
    : { matchedTotal: membership.matchedTotal, fullTotal: membership.fullTotal };
}

function copyValidity(validity: VisitApprovalEffectiveValidityCounts): VisitApprovalEffectiveValidityCounts {
  return validity.kind === 'Pending'
    ? { kind: 'Pending' }
    : {
        kind: 'Exact',
        approved: validity.approved,
        checkPending: validity.checkPending,
        needsReview: validity.needsReview,
      };
}
